import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { mapSystemUserPayloadFromForm, mapSystemUsersResponseToDto } from '../dto/system-users.dto.js';
import {
  mapSupportAccessGrantFormToPayload,
  mapSupportAccessInviteResponseToDto,
  mapSupportAccessResponseToDto,
} from '../dto/support-access.dto.js';
import {
  buildSupportAccessRows,
  validateSupportAccessGrantForm,
} from '../model/support-access.model.js';
import { buildSystemUsersTableModel } from '../model/system-user-row.model.js';
import { resolveSystemUsersAccess } from '../model/system-users-access.js';

const EMPTY_FORM = Object.freeze({
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  disabled: false,
});

const EMPTY_SUPPORT_GRANT_FORM = Object.freeze({
  label: '',
  accessLevel: 'read_only',
  expiresAt: '',
  reason: '',
});

function getUserId(value) {
  if (!value || typeof value !== 'object') {
    return '';
  }
  const id = value.id ?? value.uid;
  return id === null || id === undefined ? '' : String(id);
}

function getUserEmail(value) {
  if (!value || typeof value !== 'object') {
    return '';
  }
  return String(value.email || '').trim().toLowerCase();
}

function validateCreateForm(form) {
  const errors = {};
  const firstName = String(form.firstName || '').trim();
  const lastName = String(form.lastName || '').trim();
  const email = String(form.email || '').trim();
  const password = String(form.password || '').trim();
  const confirmPassword = String(form.confirmPassword || '').trim();

  if (!firstName) {
    errors.firstName = 'This field is required.';
  }
  if (!lastName) {
    errors.lastName = 'This field is required.';
  }
  if (!email) {
    errors.email = 'This field is required.';
  }
  if (!password) {
    errors.password = 'This field is required.';
  }
  if (!confirmPassword) {
    errors.confirmPassword = 'This field is required.';
  } else if (password !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.';
  }

  return errors;
}

function validateEditForm(form) {
  const errors = {};
  const firstName = String(form.firstName || '').trim();
  const lastName = String(form.lastName || '').trim();
  const email = String(form.email || '').trim();

  if (!firstName) {
    errors.firstName = 'This field is required.';
  }
  if (!lastName) {
    errors.lastName = 'This field is required.';
  }
  if (!email) {
    errors.email = 'This field is required.';
  }

  return errors;
}

function mapFieldErrors(requestError, allowedFields) {
  const nextFieldErrors = {};
  const details = Array.isArray(requestError?.errors) ? requestError.errors : [];

  details.forEach((item) => {
    const parameter = String(item?.parameter || '').trim();
    if (allowedFields.includes(parameter)) {
      nextFieldErrors[parameter] = String(item?.message || 'Invalid value').trim() || 'Invalid value';
    }
  });

  return nextFieldErrors;
}

export function useSystemUsers() {
  const { token, user, permissions } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTargetId, setEditingTargetId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState('tenant-users');
  const [supportRows, setSupportRows] = useState([]);
  const [isSupportLoading, setIsSupportLoading] = useState(false);
  const [hasLoadedSupportRows, setHasLoadedSupportRows] = useState(false);
  const [isSupportGrantOpen, setIsSupportGrantOpen] = useState(false);
  const [supportGrantForm, setSupportGrantForm] = useState(EMPTY_SUPPORT_GRANT_FORM);
  const [supportGrantErrors, setSupportGrantErrors] = useState({});
  const [oneTimeInviteUrl, setOneTimeInviteUrl] = useState('');

  const access = useMemo(() => resolveSystemUsersAccess(permissions), [permissions]);
  const currentUserId = getUserId(user);
  const currentUserEmail = getUserEmail(user);

  const isCurrentUserRow = useCallback(
    (row) => {
      const rowId = getUserId(row);
      const rowEmail = getUserEmail(row);

      if (currentUserId && rowId && currentUserId === rowId) {
        return true;
      }
      if (currentUserEmail && rowEmail && currentUserEmail === rowEmail) {
        return true;
      }

      return false;
    },
    [currentUserEmail, currentUserId],
  );

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.getSystemUsers(token);
      const dto = mapSystemUsersResponseToDto(response);
      setRows(buildSystemUsersTableModel(dto));
    } catch (requestError) {
      setError(requestError.message || 'Failed to load users');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const loadSupportAccess = useCallback(async ({ force = false } = {}) => {
    if (!access.canViewSupportAccess || (!force && hasLoadedSupportRows)) {
      return;
    }

    setIsSupportLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.getSupportAccessGrants(token);
      const dto = mapSupportAccessResponseToDto(response);
      setSupportRows(buildSupportAccessRows(dto));
      setHasLoadedSupportRows(true);
    } catch (requestError) {
      setError(requestError.message || 'Failed to load support access grants');
      setSupportRows([]);
      setHasLoadedSupportRows(false);
    } finally {
      setIsSupportLoading(false);
    }
  }, [access.canViewSupportAccess, hasLoadedSupportRows, token]);

  useEffect(() => {
    if (activeTab === 'support-access' && access.canViewSupportAccess) {
      loadSupportAccess();
    }
  }, [access.canViewSupportAccess, activeTab, loadSupportAccess]);

  const changeActiveTab = useCallback((tab) => {
    if (tab === 'support-access' && !access.canViewSupportAccess) {
      return;
    }

    setActiveTab(tab === 'support-access' ? 'support-access' : 'tenant-users');
    setIsCreateOpen(false);
    setEditingTargetId('');
    setIsSupportGrantOpen(false);
    setFormErrors({});
    setSupportGrantErrors({});
  }, [access.canViewSupportAccess]);

  const openDetails = useCallback((id) => {
    setIsCreateOpen(false);
    setEditingTargetId('');
    if (id) {
      navigate(`/system/users/${encodeURIComponent(String(id))}`);
    }
  }, [navigate]);

  const openCreate = useCallback(() => {
    setEditingTargetId('');
    setIsCreateOpen(true);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setError('');
    setSuccess('');
  }, []);

  const openEdit = useCallback((row) => {
    setIsCreateOpen(false);
    setEditingTargetId(row.id);
    setForm({
      firstName: row.firstName || '',
      lastName: row.lastName || '',
      email: row.email || '',
      password: '',
      confirmPassword: '',
      disabled: Boolean(row.disabled),
    });
    setFormErrors({});
    setError('');
    setSuccess('');
  }, []);

  const closeModals = useCallback(() => {
    if (isSaving) {
      return;
    }

    setIsCreateOpen(false);
    setEditingTargetId('');
    setIsSupportGrantOpen(false);
    setFormErrors({});
    setSupportGrantErrors({});
  }, [isSaving]);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const openSupportGrant = useCallback(() => {
    setIsCreateOpen(false);
    setEditingTargetId('');
    setIsSupportGrantOpen(true);
    setSupportGrantForm(EMPTY_SUPPORT_GRANT_FORM);
    setSupportGrantErrors({});
    setError('');
    setSuccess('');
  }, []);

  const closeSupportGrant = useCallback(() => {
    if (isSaving) {
      return;
    }

    setIsSupportGrantOpen(false);
    setSupportGrantErrors({});
  }, [isSaving]);

  const onSupportGrantFormChange = useCallback((key, value) => {
    setSupportGrantForm((prev) => ({ ...prev, [key]: value }));
    setSupportGrantErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const upsertSupportGrantRow = useCallback((grant) => {
    if (!grant) {
      return;
    }

    const [row] = buildSupportAccessRows([grant]);
    if (!row) {
      return;
    }

    setSupportRows((prev) => {
      const exists = prev.some((item) => item.id === row.id);
      if (!exists) {
        return [row, ...prev];
      }

      return prev.map((item) => (item.id === row.id ? { ...item, ...row } : item));
    });
    setHasLoadedSupportRows(true);
  }, []);

  const saveSupportGrant = useCallback(async (event) => {
    event.preventDefault();

    const nextErrors = validateSupportAccessGrantForm(supportGrantForm);
    if (Object.keys(nextErrors).length > 0) {
      setSupportGrantErrors(nextErrors);
      setError('Please fix validation errors.');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    setOneTimeInviteUrl('');

    try {
      const response = await adminApi.createSupportAccessGrant(
        token,
        mapSupportAccessGrantFormToPayload(supportGrantForm),
      );
      const invite = mapSupportAccessInviteResponseToDto(response);
      upsertSupportGrantRow(invite.grant);
      setIsSupportGrantOpen(false);
      setSupportGrantErrors({});
      setOneTimeInviteUrl(invite.inviteUrl);
      setSuccess('Support access grant created.');
    } catch (requestError) {
      setSupportGrantErrors(mapFieldErrors(requestError, ['label', 'accessLevel', 'expiresAt', 'reason']));
      setError(requestError.message || 'Failed to create support access grant');
    } finally {
      setIsSaving(false);
    }
  }, [supportGrantForm, token, upsertSupportGrantRow]);

  const revokeSupportGrant = useCallback(async (row) => {
    if (isSaving || !row?.id) {
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.revokeSupportAccessGrant(token, row.id, {
        reason: 'Revoked from admin UI',
      });
      const [grant] = mapSupportAccessResponseToDto({ grants: [response?.grant || response] });
      upsertSupportGrantRow(grant);
      setSuccess('Support access grant revoked.');
    } catch (requestError) {
      setError(requestError.message || 'Failed to revoke support access grant');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, token, upsertSupportGrantRow]);

  const regenerateSupportInvite = useCallback(async (row) => {
    if (isSaving || !row?.id) {
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    setOneTimeInviteUrl('');

    try {
      const response = await adminApi.regenerateSupportAccessInvite(token, row.id, {
        reason: 'Regenerated from admin UI',
      });
      const invite = mapSupportAccessInviteResponseToDto(response);
      upsertSupportGrantRow(invite.grant);
      setOneTimeInviteUrl(invite.inviteUrl);
      setSuccess('Support access invite regenerated.');
    } catch (requestError) {
      setError(requestError.message || 'Failed to regenerate support access invite');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, token, upsertSupportGrantRow]);

  const closeSupportInvite = useCallback(() => {
    setOneTimeInviteUrl('');
  }, []);

  const saveEdit = useCallback(
    async (event) => {
      event.preventDefault();

      const nextErrors = validateEditForm(form);
      if (Object.keys(nextErrors).length > 0) {
        setFormErrors(nextErrors);
        setError('Please fix validation errors.');
        return;
      }

      const isEditingCurrentUser = Boolean(editingTargetId) && isCurrentUserRow({ id: editingTargetId, email: form.email });
      if (isEditingCurrentUser && Boolean(form.disabled)) {
        setFormErrors((prev) => ({ ...prev, disabled: 'Current user cannot be disabled.' }));
        setError('Current user cannot be disabled.');
        return;
      }

      setIsSaving(true);
      setError('');
      setSuccess('');

      const body = {
        firstName: String(form.firstName || '').trim(),
        lastName: String(form.lastName || '').trim(),
        email: String(form.email || '').trim(),
        password: String(form.password || '').trim(),
      };

      try {
        const response = await adminApi.updateSystemUser(token, editingTargetId, body);
        const dto = mapSystemUsersResponseToDto({ users: [response?.user || body] });
        const updatedUser = buildSystemUsersTableModel(dto)[0];
        setRows((prev) => prev.map((row) => (row.id === editingTargetId ? { ...row, ...updatedUser, id: row.id } : row)));
        setEditingTargetId('');
        setFormErrors({});
        setSuccess('User updated.');
      } catch (requestError) {
        setFormErrors(mapFieldErrors(requestError, ['firstName', 'lastName', 'email', 'password', 'disabled']));
        setError(requestError.message || 'Failed to update user');
      } finally {
        setIsSaving(false);
      }
    },
    [editingTargetId, form, isCurrentUserRow, token],
  );

  const saveCreate = useCallback(
    async (event) => {
      event.preventDefault();

      const nextErrors = validateCreateForm(form);
      if (Object.keys(nextErrors).length > 0) {
        setFormErrors(nextErrors);
        setError('Please fix validation errors.');
        return;
      }

      setIsSaving(true);
      setError('');
      setSuccess('');

      const body = mapSystemUserPayloadFromForm(form, { includePassword: true });

      try {
        const response = await adminApi.createSystemUser(token, body);
        const dto = mapSystemUsersResponseToDto({ users: [response?.user || body] });
        const createdUser = buildSystemUsersTableModel(dto)[0];
        setRows((prev) => [...prev, createdUser]);
        setIsCreateOpen(false);
        setFormErrors({});
        setSuccess('User created.');
      } catch (requestError) {
        setFormErrors(mapFieldErrors(requestError, ['firstName', 'lastName', 'email', 'password']));
        setError(requestError.message || 'Failed to create user');
      } finally {
        setIsSaving(false);
      }
    },
    [form, token],
  );

  const toggleUserDisabled = useCallback(
    async (row, nextDisabled) => {
      if (isSaving) {
        return;
      }
      if (isCurrentUserRow(row) && nextDisabled) {
        setError('Current user cannot be disabled.');
        return;
      }

      setRows((prev) => prev.map((item) => (item.id === row.id ? { ...item, disabled: nextDisabled } : item)));
      setIsSaving(true);
      setError('');
      setSuccess('');

      try {
        await adminApi.updateSystemUser(token, row.id, { disabled: nextDisabled });
        setSuccess('User status updated.');
      } catch (requestError) {
        setRows((prev) => prev.map((item) => (item.id === row.id ? { ...item, disabled: row.disabled } : item)));
        setError(requestError.message || 'Failed to update user status');
      } finally {
        setIsSaving(false);
      }
    },
    [isCurrentUserRow, isSaving, token],
  );

  return {
    ...access,
    activeTab,
    supportRows,
    isSupportLoading,
    isSupportGrantOpen,
    supportGrantForm,
    supportGrantErrors,
    oneTimeInviteUrl,
    rows,
    isLoading,
    isSaving,
    error,
    success,
    isCreateOpen,
    editingTargetId,
    form,
    formErrors,
    setActiveTab: changeActiveTab,
    loadSupportAccess,
    openDetails,
    openCreate,
    openEdit,
    openSupportGrant,
    closeSupportGrant,
    closeModals,
    onFormChange,
    onSupportGrantFormChange,
    saveEdit,
    saveCreate,
    saveSupportGrant,
    revokeSupportGrant,
    regenerateSupportInvite,
    closeSupportInvite,
    toggleUserDisabled,
    isCurrentUserRow,
  };
}
